/**
 * Cloudinary delivery: ücretsiz planda kredi/bant genişliği için sabit dönüşümler.
 * f_auto, q_auto + boyut; URL'de zaten dönüşüm varsa dokunma.
 */

/**
 * DB'de host olmadan saklanan Cloudinary yolları: `cloudName/image/upload/...`
 * veya `//res.cloudinary.com/...` / `res.cloudinary.com/...`
 * @returns {string|null} Tam https Cloudinary URL veya bilinmiyorsa null
 */
export function coerceCloudinaryAbsoluteIfPartial(input) {
  if (!input || typeof input !== 'string') return null;
  const p = input.trim();
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return null;
  if (p.startsWith('//')) return `https:${p}`;
  if (/^res\.cloudinary\.com\//i.test(p)) {
    return `https://${p.replace(/^\/+/, '')}`;
  }
  const m = p.match(/^\/?([^/]+)\/(image|video)\/upload\/(.+)$/i);
  if (m) {
    return `https://res.cloudinary.com/${m[1]}/${m[2].toLowerCase()}/upload/${m[3]}`;
  }
  return null;
}

const CLOUDINARY_UPLOAD = /^(https:\/\/res\.cloudinary\.com\/[^/]+\/(?:image|video)\/upload\/)(.+)$/;

const PRESETS = {
  /** Logo / tablo küçük görsel — kare */
  square500: 'f_auto,q_auto,w_500,h_500,c_fill',
  /** Menü, kampanya listesi — oran korunur */
  wide800: 'f_auto,q_auto,w_800,c_limit',
  /** Büyük önizleme */
  wide1200: 'f_auto,q_auto,w_1200,c_limit',
};

export function cloudinaryDeliveryUrl(url, preset = 'wide800') {
  if (!url || typeof url !== 'string') return url;
  let trimmed = url.trim();
  const coerced = coerceCloudinaryAbsoluteIfPartial(trimmed);
  if (coerced) trimmed = coerced;
  /** Cloudinary "Strict transformations" açıksa türetilmiş URL 401/404 verebilir; ham secure_url kullan. */
  const noTransforms =
    import.meta.env.VITE_CLOUDINARY_NO_DELIVERY_TRANSFORMS === 'true' ||
    import.meta.env.REACT_APP_CLOUDINARY_NO_DELIVERY_TRANSFORMS === 'true';
  if (noTransforms) return trimmed;

  const m = trimmed.match(CLOUDINARY_UPLOAD);
  if (!m) return trimmed;
  const rest = m[2];
  if (/^(f_|q_|w_|h_|c_)/.test(rest)) return trimmed;
  /** İmzalı teslimat segmenti (s--…--); araya f_auto ekleme. */
  if (rest.startsWith('s--') || rest.includes('/s--')) return trimmed;
  const chain = PRESETS[preset] || PRESETS.wide800;
  return `${m[1]}${chain}/${rest}`;
}

function normalizePanelApiBase(rawBase) {
  let b = rawBase == null ? '' : String(rawBase).trim().replace(/\/+$/, '');
  if (!b) return '';
  if (!/^https?:\/\//i.test(b)) {
    const prod =
      typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';
    b = `${prod ? 'https://' : 'http://'}${b}`;
  }
  return b.replace(/\/+$/, '');
}

/**
 * İşletme paneli menü/kampanya: DB'deki image alanı → güvenli img src.
 * Önler: tam Cloudinary URL'nin önüne `/uploads/` eklenmesi (404).
 */
export function resolveBusinessPanelMediaUrl(imagePath, apiBaseUrl, preset = 'wide800') {
  const s =
    imagePath == null ? '' : String(imagePath).replace(/^\uFEFF/, '').trim();
  if (!s) return '/placeholder.png';

  if (/^https?:\/\//i.test(s)) {
    return cloudinaryDeliveryUrl(s, preset);
  }
  if (s.startsWith('//')) {
    return cloudinaryDeliveryUrl(`https:${s}`, preset);
  }
  if (/^res\.cloudinary\.com\//i.test(s)) {
    return cloudinaryDeliveryUrl(`https://${s.replace(/^\/+/, '')}`, preset);
  }

  const coerced = coerceCloudinaryAbsoluteIfPartial(s);
  if (coerced) {
    return cloudinaryDeliveryUrl(coerced, preset);
  }

  const embedded = s.match(/https?:\/\/res\.cloudinary\.com[^\s'"]+/i);
  if (embedded) {
    return cloudinaryDeliveryUrl(embedded[0], preset);
  }

  const base = normalizePanelApiBase(
    apiBaseUrl ||
      (typeof import.meta !== 'undefined' && import.meta.env?.REACT_APP_API_URL) ||
      (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) ||
      'http://localhost:5001'
  );

  if (s.startsWith('/uploads/')) {
    return cloudinaryDeliveryUrl(`${base}${s}`, preset);
  }
  return cloudinaryDeliveryUrl(`${base}/uploads/${s.replace(/^\/+/, '')}`, preset);
}
